import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchPrefs, putPrefs } from './api.js';
import type { Prefs } from './types.js';

export type UsePrefsResult = {
  prefs: Prefs | null;
  setPref: (patch: Partial<Prefs>) => Promise<void>;
  loading: boolean;
  error: string | null;
};

export function usePrefs(): UsePrefsResult {
  const [prefs, setPrefs] = useState<Prefs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setLoading(true);
    setError(null);
    fetchPrefs()
      .then((p) => {
        if (!mountedRef.current) return;
        setPrefs(p);
      })
      .catch((e) => {
        if (!mountedRef.current) return;
        setError((e as Error).message ?? '加载失败');
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setPref = useCallback(async (patch: Partial<Prefs>): Promise<void> => {
    try {
      const next = await putPrefs(patch);
      if (mountedRef.current) {
        setPrefs(next);
        setError(null);
      }
    } catch (e) {
      if (mountedRef.current) setError((e as Error).message ?? '保存失败');
      throw e;
    }
  }, []);

  return { prefs, setPref, loading, error };
}
